import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to:`, config.url);
    
    // Verificar se há um usuário na sessão e se está expirado
    const savedUser = localStorage.getItem('stakemind_user');
    if (savedUser && !config.url?.includes('/auth/login') && !config.url?.includes('/admin/')) {
      try {
        const parsedUser = JSON.parse(savedUser);
        const keyExpiresAt = new Date(parsedUser.key_expires_at);
        const isExpired = new Date() > keyExpiresAt;
        
        if (isExpired) {
          console.log('🚫 API bloqueada - usuário com chave expirada tentando fazer requisição');
          // Limpar dados do usuário expirado
          localStorage.removeItem('stakemind_user');
          
          // Rejeitar a requisição e forçar reload
          alert('⚠️ Licença expirada.\n\nVocê será redirecionado para o login.');
          window.location.reload();
          
          return Promise.reject(new Error('Chave de acesso expirada'));
        }
      } catch (error) {
        console.error('Erro ao verificar expiração na API:', error);
      }
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// User management
export const userAPI = {
  async createUser(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  async getAllUsers() {
    const response = await api.get('/users');
    return response.data;
  },

  async getUserById(userId) {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  async updateUser(userId, userData) {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  async deleteUser(userId) {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  async authenticateUser(email, accessKey) {
    const response = await api.post('/auth/login', { 
      email, 
      access_key: accessKey 
    });
    return response.data;
  },

  // Get user notifications
  getNotifications: async (userEmail) => {
    const response = await api.get(`/notifications/${encodeURIComponent(userEmail)}`);
    return response.data;
  },

  // Mark notification as read
  markNotificationAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  }
};

// Access key management
export const accessKeyAPI = {
  async createAccessKey(keyData) {
    const response = await api.post('/access-keys', keyData);
    return response.data;
  },

  async getAllAccessKeys() {
    const response = await api.get('/access-keys');
    return response.data;
  },

  async updateAccessKey(keyId, keyData) {
    const response = await api.put(`/access-keys/${keyId}`, keyData);
    return response.data;
  },

  async deleteAccessKey(keyId) {
    const response = await api.delete(`/access-keys/${keyId}`);
    return response.data;
  }
};

// Admin management
export const adminAPI = {
  async login(email, password) {
    const response = await api.post('/admin/login', { email, password });
    return response.data;
  },

  async getRevenue() {
    const response = await api.get('/admin/revenue');
    return response.data;
  },

  async createUser(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  async getAllUsers() {
    const response = await api.get('/users');
    return response.data;
  },

  async updateUser(userId, userData) {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  async deleteUser(userId) {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  async renewUserKey(userId, days) {
    const response = await api.put(`/users/${userId}/renew`, { validity_period: days });
    return response.data;
  }
};

export default api;